import type { DateRange } from "../types/trade";
import type { ReportTypeOption } from "../types/reports";
import { MARKET_DATA_CONNECT_GATE_BLOCKED_MESSAGE } from "./marketDataConnectGate";

export type GenerateReportBlockedReasonInput = {
  rateLimitCoolingDown: boolean;
  rateLimitRetryAfterSeconds: number;
  inflightCheckLoading: boolean;
  hasInflightReport: boolean;
  effectiveBrokerAccountCode: string;
  reportTitle: string;
  symbolRequired: boolean;
  symbol: string;
  showPerformanceReviewSymbolFields?: boolean;
  performanceReviewAllSymbols?: boolean;
  performanceReviewSymbols?: string[];
  mdBlocked: boolean;
  marketDataConnectGateBlocked?: boolean;
  marketDataHistoryBlocked?: boolean;
  marketDataHistoryBlockedMessage?: string | null;
  candleTimeframePreviewChecking?: boolean;
  /** True when locked TF × window exceeds the shared ~60k bar budget (same as simulate). */
  candleBudgetExceeds?: boolean;
  marketDataAvailabilityChecking?: boolean;
  pendingAssetClassPayload: unknown;
  pendingMarketDataSourceChoice: unknown;
  pendingConsent: unknown;
  instrumentNeedsOhlcSourceChoice?: boolean;
  selectedReportType: string;
  candleTimeframe: string;
  reportTypeOptions: ReportTypeOption[];
  dateRange: DateRange;
  weeklyReportQuotaReached?: boolean;
  weeklyReportQuotaMessage?: string | null;
};

export function getGenerateReportBlockedReason(
  input: GenerateReportBlockedReasonInput
): string | null {
  if (input.rateLimitCoolingDown) {
    return `Too many requests — wait ${input.rateLimitRetryAfterSeconds}s before generating again.`;
  }
  if (input.inflightCheckLoading) {
    return "Checking whether a report is already running…";
  }
  if (input.hasInflightReport) {
    return "A report is already generating. Wait for it to finish before starting another.";
  }
  if (input.weeklyReportQuotaReached) {
    return (
      input.weeklyReportQuotaMessage?.trim() ||
      "You've reached this week's report limit. Wait until next week, or upgrade to Plus for a higher cap."
    );
  }
  if (!input.effectiveBrokerAccountCode) {
    return "Select a broker account on step 1.";
  }
  if (!input.reportTitle.trim()) {
    return "Enter a report name on step 2.";
  }
  if (input.symbolRequired && !input.symbol.trim()) {
    return "Choose an instrument on step 2.";
  }
  if (
    input.showPerformanceReviewSymbolFields &&
    input.performanceReviewAllSymbols === false &&
    (input.performanceReviewSymbols?.length ?? 0) === 0
  ) {
    return "Choose at least one symbol, or include all symbols.";
  }
  if (input.marketDataAvailabilityChecking) {
    return "Checking market data connection for your instrument…";
  }
  if (input.candleTimeframePreviewChecking) {
    return "Checking candle timeframe availability for your date range…";
  }
  if (input.candleBudgetExceeds) {
    return "This candle timeframe needs more candles than we collect for this date range. Choose a larger timeframe.";
  }
  if (input.marketDataConnectGateBlocked) {
    return MARKET_DATA_CONNECT_GATE_BLOCKED_MESSAGE;
  }
  if (input.marketDataHistoryBlocked) {
    return (
      input.marketDataHistoryBlockedMessage?.trim() ||
      "Hyperliquid can't supply native candles at this timeframe for trades this old. Choose 1h+ or switch data source."
    );
  }
  if (input.mdBlocked) {
    return "Market data is not available for the selected instrument. Pick another symbol or fix your broker setup.";
  }
  if (input.pendingAssetClassPayload) {
    return "Confirm the asset class for your instrument (step 2).";
  }
  if (input.pendingMarketDataSourceChoice || input.instrumentNeedsOhlcSourceChoice) {
    return "Choose an OHLC listing for your instrument (step 2).";
  }
  if (input.pendingConsent) {
    return "Grant market data consent for your instrument (step 2).";
  }
  if (!input.selectedReportType) {
    return "Select a report type on step 2.";
  }
  if (!input.candleTimeframe) {
    return "Choose a candle timeframe on step 2.";
  }
  if (!input.reportTypeOptions.some((o) => o.key === input.selectedReportType)) {
    return "The selected report type is no longer available. Go back to step 2 and refresh report types.";
  }
  if (input.dateRange.startDate && !input.dateRange.endDate) {
    return "Finish selecting a date range on step 1 (end date is missing).";
  }
  if (!input.dateRange.startDate && input.dateRange.endDate) {
    return "Finish selecting a date range on step 1 (start date is missing).";
  }
  return null;
}

export function isGenerateReportReady(input: GenerateReportBlockedReasonInput): boolean {
  return getGenerateReportBlockedReason(input) === null;
}
